import { Edit } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Speciality } from '.';
import { Commission } from '../commissions';
import { CRUD_TYPE, _Locale } from '../commissions/BasicTable';
import { PlusIcon } from '../icons';

const _defaultItem = {
  id: 0,
  title: {
    ru: '',
    uz: '',
    oz: '',
  },
  commission: '',
  publicId: '',
};
const _defaultTitle = {
  ru: '',
  uz: '',
  oz: '',
};

export default function BasicTable({
  data,
  createItem,
  updateItem,
  commissions,
  changePagination,
  total,
}: {
  data: Speciality[];
  createItem: Function;
  updateItem: Function;
  changePagination: Function;
  commissions: Commission[] | null;
  total: number;
}) {
  const { t, i18n } = useTranslation();

  const [open, setOpen] = useState(false);
  const [crud, setCrud] = useState(CRUD_TYPE.CREATE);
  const [item, setItem] = useState<Speciality>({ ..._defaultItem });
  const [title, setTitle] = useState({ ..._defaultTitle });

  const handleClickOpen = (type: CRUD_TYPE, _item?: Speciality) => {
    if (type === CRUD_TYPE.CREATE) {
      setTitle({ ..._defaultTitle });
      resetItem();
    } else {
      if (_item) {
        setTitle({ ..._item.title });
        setItem({ ..._item, title });
      }
    }
    setCrud(type);
    setOpen(true);
  };

  const handleCreate = () => {
    if (crud === CRUD_TYPE.CREATE) {
      createItem({ ...item, title });
    } else {
      updateItem({ ...item, title });
    }
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const resetItem = () => {
    setItem({ ..._defaultItem });
  };

  const pageChangeHandler = (event: any, pageNumber: string | number) => {
    changePagination(pageNumber);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          width: 600,
          mx: 'auto',
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            fontSize: 22,
            marginBottom: 2,
          }}
        >
          {t(crud.toLocaleLowerCase())}
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            fontSize: 18,
          }}
        >
          <DialogContentText id="alert-dialog-slide-description"></DialogContentText>
          <TextField
            label="O'zbek tilida"
            type="text"
            sx={{
              mt: 3,
            }}
            fullWidth
            variant="outlined"
            onChange={(e) => setTitle({ ...title, oz: e.target.value })}
            defaultValue={title.oz}
          />
          <TextField
            label="Ўзбек тилида"
            type="text"
            variant="outlined"
            fullWidth
            onChange={(e) => setTitle({ ...title, uz: e.target.value })}
            sx={{
              mt: 3,
            }}
            defaultValue={title.uz}
          />
          <TextField
            label="На русском"
            type="text"
            variant="outlined"
            fullWidth
            sx={{
              mt: 3,
            }}
            onChange={(e) => {
              setTitle({ ...title, ru: e.target.value });
            }}
            defaultValue={title.ru}
          />
          <TextField
            label={t('speciality_code')}
            type="text"
            variant="outlined"
            fullWidth
            sx={{
              mt: 3,
            }}
            onChange={(e) => {
              setItem({ ...item, publicId: e.target.value });
            }}
            defaultValue={item.publicId}
          />
          {/* <TextField
            label={t('commission_code')}
            type="text"
            variant="outlined"
            fullWidth
            sx={{
              mt: 3,
            }}
            onChange={(e) => {
              setItem({ ...item, commission: e.target.value });
            }}
            defaultValue={item.commission}
          /> */}
          <FormControl
            sx={{
              mt: 3,
            }}
            fullWidth
          >
            <InputLabel id="demo-simple-select-label">{t('commissions')}</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={item.commission}
              label="Age"
              onChange={(e) => {
                setItem({ ...item, commission: e.target.value });
              }}
            >
              {commissions?.map((c: Commission) => (
                <MenuItem value={c.id} key={c.id}>
                  {c.title[i18n.language as _Locale]}
                </MenuItem>
              ))}
              {/* <MenuItem value={20}>Twenty</MenuItem> */}
              {/* <MenuItem value={30}>Thirty</MenuItem> */}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'space-between',
            marginTop: 4,
          }}
        >
          <Button onClick={handleClose} variant="outlined" size="large" color="secondary">
            {t('action.cancel')}
          </Button>
          <Button onClick={handleCreate} variant="contained" size="large" color="secondary">
            {t('action.save')}
          </Button>
        </DialogActions>
      </Dialog>
      <Box display={'flex'} justifyContent={'flex-end'}>
        <Button
          onClick={() => handleClickOpen(CRUD_TYPE.CREATE)}
          variant="contained"
          sx={{
            mb: 2,
          }}
          color="secondary"
        >
          <PlusIcon /> &nbsp;
          {t('create')}
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>{t('commission' || 'Код комиссии')}</TableCell>
              <TableCell>{t('speciality_code' || 'Код специальности')}</TableCell>
              <TableCell>{t('location.name.label')}</TableCell>
              <TableCell align="center"> </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell component="th" scope="row">
                  {row.id}
                </TableCell>
                <TableCell>
                  {
                    commissions?.filter((f) => f.id === row.commission)[0]?.title[
                      i18n.language as _Locale
                    ]
                  }
                </TableCell>
                <TableCell>{row.publicId}</TableCell>
                <TableCell>{row.title[i18n.language as _Locale]}</TableCell>
                <TableCell align="center">
                  <Button variant="outlined" onClick={() => handleClickOpen(CRUD_TYPE.UPDATE, row)}>
                    <Edit />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display={'flex'} justifyContent={'center'} my={3}>
        <Pagination
          count={total}
          variant="outlined"
          shape="rounded"
          onChange={(event, pageNumber) => pageChangeHandler(event, pageNumber)}
        />
      </Box>
    </>
  );
}
